import Model, { ModelStoreKey } from "../models/Model";
import EmittedEvent from "../types/EmittedEvent";
import EventEmitter from "./EventEmitter";
import { BatchClassConfig } from "../models/config/BatchClassConfig";
import ICrudApplicationState from "../types/ICrudApplicationState";
import ModelPassingInfo from "../types/ModelPassingInfo";
import ModelLoadedEvent from "../types/ModelLoadedEvent";
import ModelImages from "../models/ModelImages";
import eventEmitter from "./EventEmitter";
import ColumnPassingInfo from "../types/ColumnPassingInfo";
import { getObjectStoreIdString } from "../decorators/StoreIdHelper";
import { modelAlreadyExistsChecker } from "../models/ModelAlreadyExistsChecker";
import { ModelStore } from "../stores/impl/ModelStore";
import { ModelUrlState } from "./ModelUrlState";

class _CrudState implements ICrudApplicationState {
  removeModelFromCache(model: Model): void {
    const modelIndex = this.loadedModels.indexOf(model);
    if (modelIndex > -1) this.loadedModels.splice(modelIndex, 1);
    ModelStore.remove(model.storeId);
  }

  updateCachedModel(model: Model): void {
    const idx = this.loadedModels.findIndex(
      (m) => getObjectStoreIdString(m) === getObjectStoreIdString(model),
    );
    if (idx > -1) {
      this.loadedModels[idx] = model;
    } else {
      this.loadedModels.push(model);
    }
    ModelStore.set(model, true);
  }

  async createModel(modelName: string, batchClassId: string): Promise<boolean> {
    const model: Model = Model.createTest({
      name: modelName,
      batchClassIdentifier: batchClassId,
      docType: "Unknown",
    });
    // model.name = modelName;
    // model.batchClassIdentifier = batchClassId;
    // model.docType = 'Unknown';
    // model.manuallyCreated = true;

    const modelUuid = await modelAlreadyExistsChecker(modelName, batchClassId);
    if (modelUuid.length > 0) return false;
    if (
      this.loadedModels.findIndex(
        (loadedModel) =>
          getObjectStoreIdString(loadedModel) === getObjectStoreIdString(model),
      ) > -1
    ) {
      return false;
    }
    ModelStore.set(model);
    // check if model already exists
    this.loadedModels.unshift(model);
    return true;
  }
  async getModel(data: ModelPassingInfo): Promise<ModelLoadedEvent> {
    // check if model is in loadedModels list
    let modelToSend: Model | null = null;
    const cachedModel = this.loadedModels.filter(
      (m) =>
        m.storeId.name === data.modelName &&
        m.storeId.batchClassIdentifier === data.batchClassId,
    )[0];
    if (cachedModel == null) {
      try {
        const modelUuid = await modelAlreadyExistsChecker(
          data.modelName,
          data.batchClassId,
        );
        const responseModel = await ModelStore.lookupByUuid(modelUuid);
        // const responseModel = await Model.load(data.modelName, data.batchClassId)
        modelToSend = responseModel ?? null;
        if (modelToSend != null) {
          this.loadedModels.push(modelToSend);
        }
      } catch (e) {
        this.encounteredExceptions.push({
          message: "could not load model " + JSON.stringify(data),
        });
      }
    } else {
      modelToSend = cachedModel;
    }
    const loadedModelEvent: ModelLoadedEvent = {
      model: modelToSend,
      modelData: data,
    };
    eventEmitter.publish(new EmittedEvent("loadedModel", loadedModelEvent));
    return loadedModelEvent;
  }
  getModelImages(data: ModelPassingInfo): ModelImages | null {
    let modelImages: ModelImages;

    const cachedModelImagesKey: string = this.loadedModelImages.createKey(data);
    const cachedModelImages: ModelImages =
      this.loadedModelImages.data[cachedModelImagesKey];

    if (cachedModelImages == null) {
      const cachedModel: Model | undefined = ModelStore.getWithoutLookup({
        batchClassIdentifier: data.batchClassId,
        name: data.modelName,
      });
      if (!cachedModel) return null;

      const responseModel = cachedModel.getModelImages();
      modelImages = responseModel;
      this.loadedModelImages.data[cachedModelImagesKey] = modelImages;
    } else {
      modelImages = cachedModelImages;
    }
    return modelImages;
  }
  async deleteModel(modelName: string, batchClassId: string): Promise<boolean> {
    const deletedModel = { modelName, batchClassId };
    const modelLoadEvent: ModelLoadedEvent = await this.getModel({
      ...deletedModel,
    });
    if (modelLoadEvent.model == null) {
      // shit, this failed. how ???
      EventEmitter.publish(new EmittedEvent("deleteModel", deletedModel));
      return true; // whatever
    }
    const model: Model = modelLoadEvent.model!;
    const modelUuid = await modelAlreadyExistsChecker(
      model.getName(),
      model.getBatchClassIdentifier(),
    );
    let success: boolean = true;
    if (modelUuid.length > 0) {
      success = await ModelStore.delete(model.storeId);
    }
    ModelStore.remove(model.storeId);

    if (success) {
      this.removeModelFromCache(model);

      // ModelUrlState.updateUrlFragments(undefined)
      EventEmitter.publish(new EmittedEvent("deleteModel", deletedModel));
    }
    return success;
  }
  changedDocType(data: ModelPassingInfo): void {
    EventEmitter.publish(new EmittedEvent("docTypeChanged", data));
  }
  changedModelName(data: ModelPassingInfo): void {
    EventEmitter.publish(new EmittedEvent("modelNameChanged", data));
  }
  documentBoundaryRulesChanged(data: ModelPassingInfo): void {
    EventEmitter.publish(
      new EmittedEvent("documentBoundaryRulesChanged", data),
    );
  }
  columnWidthsChanged(data: ColumnPassingInfo): void {
    eventEmitter.publish(new EmittedEvent("columnWidthsChanged", data));
  }

  /**
   * Used to store Models that have already been loaded, to make life a little easier
   */
  // TODO: change to be a dictionary
  loadedModels: Model[] = [];

  private loadedModelImages: {
    data: { [key: string]: ModelImages };
    createKey(data: ModelPassingInfo): string;
  };

  private isFocusedVariable: boolean = true;
  private browserVariantHiddenProperty = "";
  private browserVariantVisibilityChange = "";

  constructor() {
    this.loadedModelImages = {
      data: {},
      createKey: (data: ModelPassingInfo) =>
        `${data.batchClassId}-${data.modelName}`,
    };
    this.getNameForVisibilityAndHidden();
    // Warn if the browser doesn't support addEventListener or the Page Visibility API
    if (
      typeof document.addEventListener === "undefined" ||
      (this.browserVariantHiddenProperty ?? "") === ""
    ) {
      console.log(
        "Cannot determine page visability. Page will always be marked as focused.",
      );
    } else {
      // Handle page visibility change
      document.addEventListener(
        this.browserVariantVisibilityChange,
        () => {
          // @ts-ignore
          this.isFocusedVariable = !document[this.browserVariantHiddenProperty];
        },
        false,
      );
    }
  }

  getNameForVisibilityAndHidden() {
    if (typeof document.hidden !== "undefined") {
      // Opera 12.10 and Firefox 18 and later support
      this.browserVariantHiddenProperty = "hidden";
      this.browserVariantVisibilityChange = "visibilitychange";
      // @ts-ignore
    } else if (typeof document.msHidden !== "undefined") {
      this.browserVariantHiddenProperty = "msHidden";
      this.browserVariantVisibilityChange = "msvisibilitychange";
      // @ts-ignore
    } else if (typeof document.webkitHidden !== "undefined") {
      this.browserVariantHiddenProperty = "webkitHidden";
      this.browserVariantVisibilityChange = "webkitvisibilitychange";
    }
  }

  isFocused(): boolean {
    return this.isFocusedVariable;
  }

  encounteredExceptions: any[] = [];

  loadModels(models: Model[]): Model[] {
    const returnModels: Model[] = [];
    ModelStore.setMany(models, false);
    models.forEach((loaded) => {
      const cachedModel = this.loadedModels.find(
        (m) => getObjectStoreIdString(m) === getObjectStoreIdString(loaded),
      );
      if (cachedModel == null) this.loadedModels.push(loaded);
      returnModels.push(cachedModel ?? loaded);
    });
    return returnModels;
  }
}

const CrudState = new _CrudState();

export { _CrudState as _CrudStateClass };
export default CrudState;
